import React from "react";
import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Button,
} from "reactstrap";

/**
 * Reusable modal with three display modes:
 *
 *  - type="image"  → renders <img src={src} /> in the body
 *  - type="video"  → renders <video src={src} autoPlay controls /> in the body
 *  - type="custom" → renders {children} in the body (no footer added automatically)
 *
 * Props:
 *   isOpen    {bool}     – controls visibility
 *   toggle    {func}     – called on backdrop click / close button
 *   title     {string}   – optional ModalHeader text (omit to hide header)
 *   size      {string}   – reactstrap Modal size: "sm" | "lg" | "xl" (default none)
 *   centered  {bool}     – vertically center the modal
 *   type      {string}   – "image" | "video" | "custom"
 *   src       {string}   – image or video URL (used for image/video types)
 *   alt       {string}   – alt text for image type
 *   closeLabel {string}  – label for the close button (default "Close")
 *   closeColor {string}  – reactstrap Button color for close (default "secondary")
 *   children  {node}     – body content for "custom" type
 */
export default function AppModal({
  isOpen,
  toggle,
  title,
  size,
  centered = false,
  type = "custom",
  src,
  alt = "",
  closeLabel = "Close",
  closeColor = "secondary",
  children,
}) {
  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      size={size}
      centered={centered}
    >
      {title && <ModalHeader toggle={toggle}>{title}</ModalHeader>}

      {type === "image" && (
        <>
          <ModalBody className="text-center">
            <img src={src} alt={alt} className="img-fluid" />
          </ModalBody>
          <ModalFooter className="text-right">
            <Button color={closeColor} onClick={toggle}>
              {closeLabel}
            </Button>
          </ModalFooter>
        </>
      )}

      {type === "video" && (
        <>
          <ModalBody>
            <div className="d-flex justify-content-center align-items-center">
              <video width="100%" height="70%" controls autoPlay>
                <source src={src} type="video/mp4" />
              </video>
            </div>
          </ModalBody>
          <ModalFooter className="text-right">
            <Button color={closeColor} onClick={toggle}>
              {closeLabel}
            </Button>
          </ModalFooter>
        </>
      )}

      {type === "custom" && children}
    </Modal>
  );
}
